import meta from "./meta.json";

export type DocMeta = {
  generatedAt: string;
  baseUrl: string;
  apiKeyPrefix: string;
  webhookSecretPrefix: string;
  maxApiKeys: number;
  apiKeyExpiries: { id: string; label: string }[];
  defaultApiKeyExpiry: string;
  viewTracker: { title: string; limits: Record<string, number | string[]> };
  rateTiers: Record<string, { label: string; perSecond: number; perMinute: number }>;
  limits: Record<string, number> & { labels: Record<string, string> };
  allowedMimeTypes: Record<string, string[]>;
  creditTypes: { id: string; label: string; spentOn: string }[];
  webhookEvents: { id: string; description: string }[];
  sharedObjectSchemas: string[];
  endpointCount: number;
  groups: {
    id: string;
    title: string;
    description: string;
    section: string | null;
    endpointIds: string[];
  }[];
};

export const API_META = meta as unknown as DocMeta;

export const groupTitle = (group: { title: string; section: string | null }) =>
  group.section === "view-tracker" ? `${API_META.viewTracker.title}: ${group.title}` : group.title;

export type DocFaq = { question: string; answer: string };

export type DocPageKind = "guide" | "group";

export type DocPage = {
  slug: string;
  title: string;
  seoTitle: string;
  seoDescription: string;
  answer: string;
  faqs?: DocFaq[];
  related: string[];
  kind: DocPageKind;
  group?: string;
};

export const DOCS_PATH = "/docs";

export const docPath = (slug: string) => (slug ? `${DOCS_PATH}/${slug}` : DOCS_PATH);

const API_FEATURE_PATH = "/features/api";

const API = API_META;
const vt = API.viewTracker.limits as Record<string, number>;
const limits = API.limits;

type GroupCopy = {
  seoTitle: string;
  seoDescription: string;
  answer: string;
  faqs: DocFaq[];
  related: string[];
};

const GROUP_COPY: Record<string, GroupCopy> = {
  account: {
    seoTitle: "Crayo Account API: Plan, Credits and Limits",
    seoDescription:
      "Reference for GET /v1/account on the Crayo API: plan, credit balances by type, storage usage and the rate limits that apply to your key.",
    answer:
      "The Crayo Account API returns the account behind an API key: its plan, credit balances by type, storage usage and rate limits. Call GET /v1/account before a batch run to confirm you have the credits and storage the run needs.",
    faqs: [
      {
        question: "Does GET /v1/account cost credits?",
        answer: `No. It is free and sits in the default rate tier of ${API.rateTiers.default.perSecond} requests per second and ${API.rateTiers.default.perMinute} per minute.`,
      },
      {
        question: "Which credit balances does the response include?",
        answer: `The credits object carries one balance per credit type: ${API.creditTypes.map((type) => type.id).join(", ")}. They are the same balances the Crayo app spends.`,
      },
      {
        question: "How do I check storage headroom?",
        answer:
          "Read storage.used_bytes, storage.trashed_bytes and storage.allowed_bytes. Uploads, imports and generated media all count toward allowed_bytes until you delete them.",
      },
      {
        question: "Are rate limits part of the response?",
        answer:
          "Yes. The rate_limit object lists requests per second and per minute for the default, heavy, polling and uploads tiers, plus the raw, read, write and batch tiers used by View Tracker.",
      },
    ],
    related: [docPath("getting-started"), docPath("assets"), API_FEATURE_PATH],
  },
  assets: {
    seoTitle: "Crayo Assets and Uploads API Reference",
    seoDescription:
      "Import media from a URL or upload bytes through a signed URL, then list, rename and delete the assets every other Crayo API endpoint reads from.",
    answer:
      "The Crayo Assets API manages the media library that every other endpoint reads from. Import a file from a public https URL with one call, or upload bytes directly through a signed URL in three steps, and get back an asset id.",
    faqs: [
      {
        question: "Should I import from a URL or upload directly?",
        answer: `POST /v1/assets downloads a public https file up to ${limits.labels.maxImportBytes} in one call. Larger files go through the upload flow, which accepts video up to ${limits.labels.maxVideoBytes}, audio up to ${limits.labels.maxAudioBytes} and images up to ${limits.labels.maxImageBytes}.`,
      },
      {
        question: "Do uploads cost credits?",
        answer:
          "No. Getting media in spends storage, not credits. A file that would push you over your storage quota is rejected with 402 STORAGE_LIMIT_EXCEEDED, and DELETE /v1/assets/{id} frees space.",
      },
      {
        question: "How does the three-step upload work?",
        answer: `Declare the file with POST /v1/uploads, PUT the raw bytes to the signed URL it returns within ${limits.uploadUrlSeconds / 60} minutes using the content-type header it hands back, then call POST /v1/uploads/{id}/complete to turn the file into an asset.`,
      },
      {
        question: "Which file types are accepted?",
        answer: `Images: ${API.allowedMimeTypes.image.join(", ")}. Audio: ${API.allowedMimeTypes.audio.join(", ")}. Video: ${API.allowedMimeTypes.video.join(", ")}.`,
      },
    ],
    related: [docPath("projects"), docPath("autoclip"), docPath("getting-started")],
  },
  generation: {
    seoTitle: "Crayo Generation API: Images, Icons, Narration",
    seoDescription:
      "Generate images, icon sets, face swaps, narration and voice changes through the Crayo API. Every result is stored as an asset you can place in a project.",
    answer:
      "The Crayo Generation API creates images, icons, face swaps, narration and voice changes, and stores each result as an asset. Image endpoints cost 1 image credit and audio endpoints cost 1 voiceover credit per second.",
    faqs: [
      {
        question: "What does each generation endpoint cost?",
        answer:
          "Image, icon and face swap calls cost 1 image credit each, and the bulk icon endpoint costs 1 image credit per concept. Narration costs 1 voiceover credit per second of audio and the voice changer costs 1 voiceover credit per second of the source asset.",
      },
      {
        question: "Which voice ids can I pass?",
        answer:
          "Only ids returned by GET /v1/voices. The voiceover generator and the voice changer reject any other voice_id, so resolve a voice from the catalog first.",
      },
      {
        question: "Which image models and aspect ratios are available?",
        answer:
          "POST /v1/image-generator accepts black-forest-labs/flux-schnell, bytedance/seedream-4.5, bytedance/seedream-5-lite, ideogram-ai/ideogram-v3-turbo, bria/image-3.2, google/nano-banana and google/nano-banana-pro, with aspect ratios 1:1, 16:9, 9:16, 4:3 and 3:4.",
      },
      {
        question: "When are narration credits charged?",
        answer:
          "Before the audio is produced. The cost is estimated from the script length at roughly 750 characters per minute. The voice changer charges up front from the asset duration, which is why its source must be an asset you own.",
      },
    ],
    related: [docPath("catalogs"), docPath("projects"), docPath("assets")],
  },
  catalogs: {
    seoTitle: "Crayo Catalogs API: Voices, Styles and Music",
    seoDescription:
      "List the narration voices, voice languages, burned-in subtitle styles and background music tracks that Crayo API projects and generators accept.",
    answer:
      "The Crayo Catalogs API lists the voices, narration languages, subtitle styles and background music tracks you can reference from other endpoints. All four endpoints are free and return the ids that projects and generators accept.",
    faqs: [
      {
        question: "Do catalog endpoints cost credits?",
        answer: `No. All four are free and use the default rate tier of ${API.rateTiers.default.perMinute} requests per minute.`,
      },
      {
        question: "How do I filter the voice list?",
        answer:
          "GET /v1/voices accepts gender, accent, search, limit and offset query parameters. Each voice comes back with a preview_url you can play before choosing it.",
      },
      {
        question: "Which music genres can I filter by?",
        answer:
          "GET /v1/music accepts genre set to recommended, rap, anime, sad, classical, quirky, chill or vlog, plus limit and offset.",
      },
      {
        question: "Where do style ids and track ids get used?",
        answer:
          "Pass a style_id from GET /v1/subtitle-styles as subtitles.style_id, and a track_id from GET /v1/music as music.track_id, when creating or updating a project.",
      },
    ],
    related: [docPath("generation"), docPath("projects"), docPath("getting-started")],
  },
  transcripts: {
    seoTitle: "Crayo Transcripts API: Word-Level Timings",
    seoDescription:
      "Transcribe an audio or video asset through the Crayo API and get the full text with word-level start and end times in milliseconds.",
    answer:
      "The Crayo Transcripts API transcribes an audio or video asset you own and returns the full text with word-level timings in milliseconds. It costs 1 voiceover credit per second of media, and repeating the request for the same asset returns the cached transcript for free.",
    faqs: [
      {
        question: "What does a transcript cost?",
        answer:
          "1 voiceover credit per second of media, charged when the request is accepted. Transcripts are cached on the asset, so a second request for the same asset_id costs nothing.",
      },
      {
        question: "How long can the media be?",
        answer: `At most ${limits.maxTranscriptMinutes} minutes. Longer media returns 400 MEDIA_TOO_LONG, and an asset with no known duration returns 400 MEDIA_DURATION_UNKNOWN.`,
      },
      {
        question: "What is in the response?",
        answer:
          "The transcript object carries asset_id, the detected language, the full text and a words array where each word has start_ms and end_ms.",
      },
      {
        question: "Which rate limit applies?",
        answer: `POST /v1/transcripts is in the heavy tier: ${API.rateTiers.heavy.perSecond} requests per second and ${API.rateTiers.heavy.perMinute} per minute per key.`,
      },
    ],
    related: [docPath("assets"), docPath("autoclip"), docPath("projects")],
  },
  projects: {
    seoTitle: "Crayo Projects API: Compose and Render Video",
    seoDescription:
      "Build a video from scenes, narration, music, text and captions with the Crayo Projects API, then render it with the export endpoint.",
    answer:
      "The Crayo Projects API composes scenes, narration, music, text and captions into a video spec, then renders it with POST /v1/projects/{id}/export. Creating a project is free and the export costs 1 export credit per second of finished video.",
    faqs: [
      {
        question: "What does a project cost?",
        answer:
          "Creating and updating a project is free unless captions are enabled, in which case transcribing the narration costs 1 voiceover credit per second. Exporting costs 1 export credit per second of finished video, charged when the render is queued.",
      },
      {
        question: "Can I export projects made in the Crayo editor or by AutoClip?",
        answer:
          "Yes. GET /v1/projects lists every project on the account with a source of api or app, and any of them can be renamed and exported. Scenes, narration, music, texts and subtitles can only be changed on projects this API created.",
      },
      {
        question: "How are scenes timed?",
        answer: `Scenes are measured in milliseconds, play in order and may not overlap. A project holds 1 to ${limits.maxScenes} scenes, each scene ends at most ${limits.maxProjectMinutes} minutes in, and the end_ms of the last scene is the length of the finished video.`,
      },
      {
        question: "Can I create or export several projects in parallel?",
        answer: `One create or export runs per account at a time, and a second one answers 429 CONCURRENT_REQUEST. An account holds up to ${limits.maxProjects} projects and ${limits.maxConcurrentExports} running exports.`,
      },
    ],
    related: [docPath("exports"), docPath("assets"), docPath("catalogs")],
  },
  exports: {
    seoTitle: "Crayo Exports API: Track Renders and Files",
    seoDescription:
      "Poll render status and collect the finished video file through the Crayo Exports API, or let the export.completed webhook deliver it.",
    answer:
      "The Crayo Exports API tracks renders queued from projects. Poll GET /v1/exports/{id} until status is completed or failed, or register an export.completed webhook and read the finished asset from its payload.",
    faqs: [
      {
        question: "Which statuses can an export have?",
        answer:
          "Status moves through queued and processing, then ends as completed or failed. The progress field reports percent complete while it runs.",
      },
      {
        question: "Where is the finished file?",
        answer:
          "Once status is completed, GET /v1/exports/{id} also returns an asset object with a downloadable url, and the same asset lands in your library.",
      },
      {
        question: "What happens when a render fails?",
        answer:
          "The export carries an error field with a plain-language reason. Credits charged when the render was queued stay spent.",
      },
      {
        question: "How often can I poll?",
        answer: `Both export endpoints are in the polling tier: ${API.rateTiers.polling.perSecond} requests per second and ${API.rateTiers.polling.perMinute} per minute. Webhooks are the recommended way to learn that a render finished.`,
      },
    ],
    related: [docPath("projects"), docPath("webhooks"), docPath("autoclip")],
  },
  autoclip: {
    seoTitle: "Crayo AutoClip API: Long Video to Vertical Clips",
    seoDescription:
      "Turn a long video asset into captioned vertical shorts with the Crayo AutoClip API. Create a job, poll it or use webhooks, then export each clip.",
    answer:
      "The Crayo AutoClip API turns a long video asset into vertical shorts cropped to the speaker and captioned. Submit a job with POST /v1/autoclip, poll it or wait for the autoclip.completed webhook, then export each clip through the projects export endpoint.",
    faqs: [
      {
        question: "What does an AutoClip job cost?",
        answer:
          "1 video credit per requested clip, charged when the job starts. The credits stay spent if the job finds fewer moments than you asked for.",
      },
      {
        question: "How long can the source video be, and how many clips can I ask for?",
        answer: `The source must be between ${limits.autoclipMinSourceMinutes} minute and ${limits.autoclipMaxSourceHours} hours long. clip_count accepts ${limits.minClipCount} to ${limits.maxClipCount} and clip_length is 30, 60 or 90 seconds. An account can have ${limits.maxActiveAutoclips} jobs running at once.`,
      },
      {
        question: "Which edit levels and layouts are available?",
        answer:
          "edit_level is clips, captions, cropped or full. formats takes 1 to 5 of streamer, split, black-box, title-subtitles and full-video. mode is speech or energy, and energy only works with edit_level clips.",
      },
      {
        question: "How do I download the clips?",
        answer:
          "Every finished clip is a project on your account. GET /v1/autoclip/{id} lists each clip with a project_id, which you pass to POST /v1/projects/{id}/export to render it and get a downloadable asset.",
      },
    ],
    related: [docPath("assets"), docPath("exports"), docPath("webhooks"), API_FEATURE_PATH],
  },
  "speech-enhancer": {
    seoTitle: "Crayo Speech Enhancer API: Clean Speech Stems",
    seoDescription:
      "Separate a recording into speech, music and background stems with the Crayo Speech Enhancer API, then export a mix at the levels you choose.",
    answer:
      "The Crayo Speech Enhancer API cleans the speech in an audio or video asset and separates it into speech, music and background stems. Create a job, wait for speech_enhancer.completed, then export a mix at the levels you choose.",
    faqs: [
      {
        question: "What does an enhancement cost?",
        answer: `1 voiceover credit per second of the source asset, charged when the job starts. Sources can be up to ${limits.enhancerMaxSourceMinutes} minutes long and need a known duration.`,
      },
      {
        question: "How long does a job take and what comes back?",
        answer:
          "A typical recording finishes in one to three minutes. GET /v1/speech-enhancer/{id} then returns a URL for each stem as lossless WAV plus a small AAC preview, the current levels, and every export made from it.",
      },
      {
        question: "Which export formats are supported?",
        answer:
          "POST /v1/speech-enhancer/{id}/export accepts speech, music and background levels and a format of mp4, wav or mp3. Video sources come back as the original video with the new audio, and identical levels reuse the earlier export.",
      },
      {
        question: "What does deleting an enhancement remove?",
        answer:
          "DELETE /v1/speech-enhancer/{id} removes the enhancement and its stems and cancels a queued job. Exports already made from it and the source asset stay in your library.",
      },
    ],
    related: [docPath("assets"), docPath("webhooks"), docPath("generation")],
  },
  "view-tracker-raw": {
    seoTitle: "Crayo View Tracker Raw API: Live Social Lookups",
    seoDescription:
      "Read profiles, posts, feeds, comments and follower lists live from Instagram, YouTube, TikTok, Twitter and Facebook for 1 tracker credit per request.",
    answer:
      "The Crayo View Tracker raw endpoints read a profile, post, feed, comment page or follower list live from Instagram, YouTube, TikTok, Twitter or Facebook for 1 tracker credit per request. You call, you schedule and you store the readings.",
    faqs: [
      {
        question: "When is a raw lookup charged or refunded?",
        answer:
          "1 tracker credit is charged before the platform is called. A lookup that fails on our side is refunded, and a missing or private target keeps the credit.",
      },
      {
        question: "Which platforms do raw endpoints support?",
        answer:
          "Profile, post, status, about, verify and history lookups cover instagram, youtube, tiktok, twitter and facebook, with the platform in the path. Feed, comment and follower endpoints support the platforms listed on each one.",
      },
      {
        question: "What are the raw rate limits?",
        answer: `Every raw lookup shares the raw tier of ${API.rateTiers["vt-raw"].perSecond} requests per second and ${API.rateTiers["vt-raw"].perMinute} per minute per account. When platform capacity is saturated you get 503 UPSTREAM_BUSY with a Retry-After header and the credit is refunded.`,
      },
      {
        question: "How does pagination work?",
        answer: `Paged responses carry a pagination object with has_more and next_cursor. Pass next_cursor back unchanged as the cursor query parameter and stop when has_more is false. Feeds return up to ${vt.maxFeedLimit} items per page and comments up to ${vt.maxCommentLimit}.`,
      },
    ],
    related: [docPath("view-tracker-managed"), docPath("webhooks"), docPath("account")],
  },
  "view-tracker-managed": {
    seoTitle: "Crayo View Tracker Managed API: Scheduled Trackers",
    seoDescription:
      "Create trackers that refresh on your cadence, keep every reading, push webhooks and expose snapshots, events, posts and insights through the Crayo API.",
    answer:
      "The Crayo View Tracker managed endpoints create trackers that refresh on your cadence, keep every reading and push webhooks. Creating a tracker is free, each refresh costs 1 tracker credit, and every stored read is free.",
    faqs: [
      {
        question: "How often does a tracker refresh?",
        answer: `interval_minutes accepts ${vt.minIntervalMinutes} to ${vt.maxIntervalMinutes} and defaults to ${vt.defaultIntervalMinutes}. Each refresh costs 1 credit, so the monthly cost is 43,200 divided by the interval. Profile trackers keep ${(API.viewTracker.limits.depthOptions as string[]).join(", ")} newest posts up to date, ${vt.defaultMaxPosts} by default.`,
      },
      {
        question: "What are the caps?",
        answer: `${vt.activeTargetCap} active trackers per account, ${vt.maxBatchTargets} targets per batch create, ${vt.maxConcurrentRefreshes} refreshes running at once and ${vt.manualRefreshSeconds} seconds between manual refreshes of one tracker. Creating trackers is one request at a time per account.`,
      },
      {
        question: "Which webhooks do managed trackers send?",
        answer:
          "tracker.refreshed with the latest reading, snapshot, new posts and removed post ids; tracker.refresh_failed; tracker.paused; tracker.alert for viral, milestone, removed, new_post and follower_drop alerts; and credits.low once per cycle.",
      },
      {
        question: "How long are readings kept?",
        answer: `Snapshots are kept hourly for the last 14 days and daily beyond that, events for 90 days. A tracker nobody reads for 30 days pauses as paused_dormant and any read wakes it. Raw history lookups go back ${vt.historyDays} days.`,
      },
    ],
    related: [docPath("view-tracker-raw"), docPath("webhooks"), docPath("account")],
  },
  webhooks: {
    seoTitle: "Crayo Webhooks API: Signed Event Delivery",
    seoDescription:
      "Register https endpoints that receive signed POST deliveries when renders, AutoClip jobs, speech enhancements and tracker refreshes finish.",
    answer:
      "The Crayo Webhooks API registers https endpoints that receive a signed POST when a render, AutoClip job, speech enhancement or tracker refresh finishes. Verify the crayo-signature header with the secret returned once at registration.",
    faqs: [
      {
        question: "Which events can I subscribe to?",
        answer: API.webhookEvents.map((event) => event.id).join(", ") + ".",
      },
      {
        question: "How do I verify a delivery?",
        answer:
          "Read the crayo-signature header, which has the form t=<unix seconds>,v1=<hex>. Compute HMAC-SHA256 over the string <t>.<raw body> with your webhook secret and compare it to v1 using a constant-time comparison.",
      },
      {
        question: "What happens when my endpoint is down?",
        answer: `A delivery has ${limits.webhookTimeoutSeconds} seconds to get a 2xx response and is attempted up to ${limits.webhookAttempts} times, backing off exponentially from ${limits.webhookBackoffSeconds} seconds. An endpoint that fails ${limits.webhookDisableAfterFailures} times in a row is disabled and must be deleted and registered again. The delivery id is stable across retries.`,
      },
      {
        question: "How many endpoints can I register, and how do I rotate a secret?",
        answer: `Up to ${limits.maxWebhookEndpoints} endpoints per account. The secret is returned once by POST /v1/webhooks and masked afterwards, so to rotate it delete the endpoint and register it again.`,
      },
    ],
    related: [docPath("exports"), docPath("autoclip"), docPath("view-tracker-managed")],
  },
};

const GUIDE_PAGES: DocPage[] = [
  {
    slug: "getting-started",
    kind: "guide",
    title: "Getting started",
    seoTitle: "Crayo API Getting Started: Keys, Auth and Limits",
    seoDescription:
      "Create an API key on the Crayo Developers page, send it as a bearer token to api.crayo.ai, and learn the rate limits, error envelope and webhooks.",
    answer: `The Crayo API is a JSON REST API at ${API.baseUrl} authenticated with a bearer key created on the Developers page of the Crayo app. Every request sends an Authorization: Bearer header with a key that starts with ${API.apiKeyPrefix}, and errors come back as a success false envelope with a stable code.`,
    faqs: [
      {
        question: "Where do I get an API key?",
        answer: `Open the Developers page in the Crayo app at crayo.ai/developers and create a key. An account can hold up to ${API.maxApiKeys} active keys, each with its own name and an expiry of ${API.apiKeyExpiries.map((expiry) => expiry.label.toLowerCase()).join(", ")}. Only a hash is stored, so copy the key when it is shown.`,
      },
      {
        question: "What is the base URL?",
        answer: `${API.baseUrl}. Every path in these docs is relative to it, for example GET ${API.baseUrl}/v1/account.`,
      },
      {
        question: "When are credits charged?",
        answer:
          "Credits are spent when a request is accepted, not when it finishes well, so a failed render or an AutoClip job that finds fewer clips leaves them spent. View Tracker is the exception: a lookup that fails on our side is refunded.",
      },
      {
        question: "What happens when I go over a rate limit?",
        answer:
          "The response is 429 with code RATE_LIMITED and a Retry-After header in seconds. Every response carries RateLimit-Limit, RateLimit-Remaining and RateLimit-Reset headers so you can pace requests before that happens.",
      },
    ],
    related: [docPath("account"), docPath("webhooks"), docPath("openapi"), API_FEATURE_PATH],
  },
  {
    slug: "openapi",
    kind: "guide",
    title: "OpenAPI specification",
    seoTitle: "Crayo API OpenAPI 3.1 Specification",
    seoDescription:
      "Download the Crayo API OpenAPI 3.1 document covering every endpoint, parameter, request body, response and error code, ready for Postman or client generators.",
    answer: `The Crayo API publishes an OpenAPI 3.1 document at crayo.ai/docs/openapi.json that describes all ${API.endpointCount} endpoints with their parameters, request bodies, example responses and error codes. Import it into an API client or a code generator to get a typed SDK.`,
    faqs: [
      {
        question: "Which OpenAPI version is the document?",
        answer:
          "OpenAPI 3.1.0. The info block names the API Crayo API, the servers list points at the production base URL, and the security scheme is HTTP bearer authentication.",
      },
      {
        question: "What extra fields does each operation carry?",
        answer:
          "Every operation has an x-credits string describing what the call costs and an x-rate-limit object with its tier, requests per second and requests per minute. Tags match the endpoint groups in these docs.",
      },
      {
        question: "Are the shared View Tracker objects in the schema?",
        answer: `Yes. components.schemas holds an Error schema plus ${API.sharedObjectSchemas.length} shared objects such as ${API.sharedObjectSchemas.slice(0, 4).join(", ")}, each with field descriptions and an example.`,
      },
      {
        question: "How are errors described?",
        answer:
          "Each operation lists its error statuses with example bodies keyed by code, including the shared 401 MISSING_API_KEY and UNAUTHORIZED, 429 RATE_LIMITED and 500 SERVER_ERROR responses.",
      },
    ],
    related: [docPath("getting-started"), docPath("account"), API_FEATURE_PATH],
  },
];

const GROUP_PAGES: DocPage[] = API.groups.map((group) => {
  const copy = GROUP_COPY[group.id];
  return {
    slug: group.id,
    kind: "group",
    group: group.id,
    title: groupTitle(group),
    seoTitle: copy?.seoTitle ?? `Crayo ${groupTitle(group)} API Reference`,
    seoDescription: copy?.seoDescription ?? group.description,
    answer: copy?.answer ?? group.description,
    faqs: copy?.faqs,
    related: copy?.related ?? [docPath("getting-started"), API_FEATURE_PATH],
  };
});

export const DOC_PAGES: DocPage[] = [GUIDE_PAGES[0], ...GROUP_PAGES, GUIDE_PAGES[1]];

export const getDocPage = (slug: string) => DOC_PAGES.find((page) => page.slug === slug);
